import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { fromJS } from 'immutable';
import { push, replace } from 'connected-react18-router';
import moment from 'moment';

import { isResearcher } from 'src/utils/checkPermissions';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { getCompanyInfo } from '../../actions/companyDetail';
import { deleteAddonProject } from '../../actions/company/buyer/addOnProject/main';
import { findUsers, getSubsidiaries, handleUpdateCompany, saveInfo } from '../../actions/company/buyer/info';
import { fetchBuyerStatuses } from '../../actions/statuses';
import { getBuyers } from '../../actions/company/companyTarget';
import AutoCompleteContainer from '../AutoComplete';
import CompanyBuyerRouting from '../../routing/CompanyBuyerRouting';
import { CompanyBuyer } from '../../components/Company/CompanyBuyer/index';
import stopProp from '../../decorators/stopPropagation';
import preventDefault from '../../decorators/preventDefault';
import confirm from '../decorators/confirm';
import { toJS, unwrap } from '../../utils/ChangeSpy';
import config from '../../config';
import { usePopupContext } from '../../contexts/PopupContext';

const HOURS_LIMIT = 24;
const ALLOWED_ROLES = [config.ADMINISTRATOR, config.DIRECTOR, config.FINANCE_ADMIN];

const CompanyBuyerContainer = ({
  type,
  buyer,
  loading,
  location,
  statuses,
  buyerProjects,
  isDuplicateCompany,
  match: {
    params: { companyId, companySlug },
  },
  user,
  company,
  history,
  getCompanyInfo,
  deleteAddonProject,
  handleUpdateCompany,
  getSubsidiaries,
  findUsers,
  fetchBuyerStatuses,
  push,
  replace,
  getBuyers,
}) => {
  const popupContext = usePopupContext();
  const prevLoadingRef = useRef(loading);

  const tryRedirect = useCallback(() => setTimeout(() => replace(`/company/${companyId}/buyer/marketing`), 0), [
    companyId,
    replace,
  ]);

  const checkRedirection = useCallback(
    prevLoading => {
      if (loading === true) return;
      if (prevLoading !== undefined && prevLoading === false) return;

      // Tolerate an optional `company_slug` segment between the id and `/buyer` (e.g.
      // `/company/123/acme-corp/buyer`) - still counts as the "bare" buyer page.
      if (new RegExp(`^/company/${companyId}(/[^/]+)?/buyer/?$`).test(location.pathname)) {
        tryRedirect();
      }
    },
    [loading, location.pathname, companyId, tryRedirect],
  );

  const checkIfBuyer = useCallback(() => {
    if (popupContext.hasPopup()) return;
    if (loading) return;

    if (!type) {
      if (isNaN(companyId)) return push('/');

      const isResearcherUser = isResearcher(user.getIn(['roles', 0, 'slug']));

      if (isResearcherUser) {
        popupContext.openPopup('InformationPopup', {
          message: {
            texts: ["You don't have permission for this action"],
            className: 'text-danger',
          },
          header: (
            <span>
              <i aria-hidden="true" className="fa fa-exclamation-triangle text-danger" />
              Error Alert
            </span>
          ),
        });

        popupContext.onClosePopup(() => push(`/company/${companyId}`));

        return;
      }

      popupContext.openPopup('BuyerRolePopup', { companyId });
      popupContext.onClosePopup(() => {
        if (!type) {
          return push(`/company/${companyId}`);
        }

        return getSubsidiaries({ companyId });
      });
    }
  }, [popupContext, loading, type, companyId, user, push, getSubsidiaries]);

  useEffect(() => {
    checkIfBuyer();
    getSubsidiaries({ companyId });
    checkRedirection(undefined);
    fetchBuyerStatuses();
    getBuyers({ companyId });
  }, []);

  useEffect(() => {
    const prevLoading = prevLoadingRef.current;

    prevLoadingRef.current = loading;
    checkIfBuyer();
    checkRedirection(prevLoading);
  }, [loading, location.pathname]);

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      handleUpdateCompany({ name, value });
    },
    [handleUpdateCompany],
  );

  const handleSuggestChange = useCallback(
    ({ name, value }) => {
      handleUpdateCompany({ name, value });
    },
    [handleUpdateCompany],
  );

  const handleRemoveAddonProjectBase = useCallback(
    (_, addonProjectId) => {
      deleteAddonProject({
        addonProjectId,
        afterSuccess: () => {
          popupContext.closePopup();
          history.replace(`/company/${companyId}/buyer/add-on-project`);
          setTimeout(() => {
            getCompanyInfo({ companyId });
          }, 100);
        },
      });
    },
    [deleteAddonProject, popupContext, history, companyId, getCompanyInfo],
  );

  const handleRemoveAddonProject = useCallback(
    preventDefault(
      stopProp(confirm('Are you sure you wish to delete current project?', handleRemoveAddonProjectBase, popupContext)),
    ),
    [handleRemoveAddonProjectBase, popupContext],
  );

  const handleAddAddonProject = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      popupContext.openPopup('AddAddonProjectPopup', { companyId });
    },
    [popupContext, companyId],
  );

  const handleUpdateHighStatus = useCallback(() => {
    const updateAt = buyer.getIn(['highStatusUpdated']);
    const roles = toJS(user).roles.map(item => item.slug);
    const canEditWithoutExpiration = ALLOWED_ROLES.some(role => roles.includes(role));
    const statusUpdatedHoursPassed = moment().diff(updateAt, 'hours');

    popupContext.openPopup('UpdateHighStatusPopup', {
      statuses,
      highStatus: unwrap(buyer.getIn(['buyerHighStatus'])),
      currentStatus: unwrap(buyer.getIn(['buyerCurrentStatus'])),
      highStatusUpdateAt: updateAt ? moment(updateAt) : '',
      companyId,
      type: 'buyer',
      directorEmail: company.get('recordOwnerEmail'),
      isStatusExpired: canEditWithoutExpiration ? false : statusUpdatedHoursPassed > HOURS_LIMIT,
    });
  }, [buyer, user, statuses, companyId, company, popupContext]);

  if (loading || !user) return null;

  const isResearcherUser = isResearcher(user.getIn(['roles', 0, 'slug']));
  const idName = 'buyerHarvcoLeadId';
  const harvcoSuggest = (
    <>
      <div className="form-group form-inline-block w160">
        <label htmlFor={idName}>HarvCo Contact</label>
        <AutoCompleteContainer
          idName={idName}
          idValue={buyer.get(idName)}
          inputProps={{
            id: idName,
            className: 'form-control input-sm',
            placeholder: 'HarvCo Contact',
            disabled: { isResearcherUser },
          }}
          onChange={handleSuggestChange}
          onFind={findUsers}
          suggestions={buyer.get('suggests')}
          suggestionsName="suggests"
          value={buyer.getIn('buyerUserSuggest'.split('.'))}
          valueName="buyerUserSuggest"
        />
      </div>
    </>
  );

  return (
    <CompanyBuyer
      buyer={buyer}
      buyerProjects={buyerProjects}
      companyId={companyId}
      companySlug={companySlug}
      harvcoSuggest={harvcoSuggest}
      isDuplicateCompany={isDuplicateCompany}
      location={location}
      onAddAddonProject={handleAddAddonProject}
      onChange={handleChange}
      onHighStatusUpdate={handleUpdateHighStatus}
      onRemoveAddonProject={handleRemoveAddonProject}
      statuses={statuses}
      type={type}
      userRole={user.getIn(['roles', 0, 'slug'], null)}
    >
      <CompanyBuyerRouting />
    </CompanyBuyer>
  );
};

CompanyBuyerContainer.propTypes = {
  isDuplicateCompany: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    loading: state.targetCompany.info.get('isFetching') || !state.targetCompany.buyer.info.get('loaded'),
    buyer: state.targetCompany.buyer.info,
    company: state.targetCompany.target,
    user: state.auth.get('user'),
    type: state.targetCompany.buyer.info.get('buyerType'),
    statuses: fromJS(state.statuses.get('buyerStatuses')),
    projectData: fromJS(state.project.projectData),
    buyerProjects: state.targetCompany.buyer.addOnProject.buyerProjects,
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
  };
}

export { CompanyBuyerContainer };
export default connect(
  mapStateToProps,
  {
    getCompanyInfo,
    deleteAddonProject,
    handleUpdateCompany,
    getSubsidiaries,
    findUsers,
    saveInfo,
    fetchBuyerStatuses,
    push,
    replace,
    getBuyers,
  },
  mergeProps,
  connectOptions,
)(memo(CompanyBuyerContainer));
